import type { Category, Finding, ScoreReason } from './types';

const CATEGORIES: Category[] = ['hardware', 'browser', 'network', 'behaviour'];

/**
 * Bot score.
 *
 * Model (kept deliberately simple and auditable, matching the reference site):
 *  - each category sums its findings' scoreContribution
 *  - low-confidence findings contribute ZERO (they are shown, but must never
 *    inflate the number, so speculative heuristics cannot manufacture a verdict)
 *  - the total is clamped to 0..100
 *  - a category whose subtotal reaches HIGH is reported at 'high'; the global
 *    cap still applies
 *
 * We never expose which rule fired or its threshold through the public API.
 */
export function computeScore(findings: Finding[]): { score: number; reasons: ScoreReason[] } {
  const subtotals = new Map<Category, number>();
  for (const c of CATEGORIES) subtotals.set(c, 0);

  for (const f of findings) {
    if (f.confidence === 'low') continue;
    if (f.scoreContribution <= 0) continue;
    subtotals.set(f.category, (subtotals.get(f.category) ?? 0) + f.scoreContribution);
  }

  const score = Math.max(0, Math.min(100, [...subtotals.values()].reduce((a, b) => a + b, 0)));

  const reasons: ScoreReason[] = CATEGORIES.map((category) => {
    const subtotal = subtotals.get(category) ?? 0;
    return { category, level: levelFor(subtotal), score: subtotal };
  });

  return { score, reasons };
}

function levelFor(subtotal: number): 'low' | 'medium' | 'high' {
  if (subtotal <= 0) return 'low';
  if (subtotal >= BOT_THRESHOLD) return 'high';
  return 'medium';
}

/** A single category reaching this subtotal is enough to be called high. */
export const BOT_THRESHOLD = 50;

/** Overall score at or above which we render the HIGH badge. */
export const HIGH_SCORE = 50;
export const MEDIUM_SCORE = 20;

/**
 * Public, threshold-free view of reasons.
 *
 * The reference site explains "which layer produced the score" but withholds
 * thresholds "so the output stays useful for debugging without reading like a
 * bypass guide". We keep that property: the public API exposes the coarse
 * category/level/score only.
 */
export function publicReasons(reasons: ScoreReason[]): ScoreReason[] {
  return reasons.map((r) => ({ ...r }));
}
